import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notebooksService } from '@/services/collections/notebooks';
import { useAuth } from '@/contexts/AuthContext';
import type { CreateNotebookInput } from '@/types/notebook';

export const useMyNotebooks = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notebooks', 'owned', user?.uid],
    queryFn: () => notebooksService.getOwned(user!.uid),
    enabled: !!user,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const useSharedNotebooks = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notebooks', 'shared', user?.email],
    queryFn: () => notebooksService.getSharedWithMe(user!.email!),
    enabled: !!user?.email,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const usePendingNotebookInvitations = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notebooks', 'pending', user?.email],
    queryFn: () => notebooksService.getPendingInvitations(user!.email!),
    enabled: !!user?.email,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const useCreateNotebook = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateNotebookInput) =>
      notebooksService.create(input, user!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
    },
  });
};

export const useDeleteNotebook = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notebooksService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
    },
  });
};

export const useRespondToNotebookInvitation = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      notebooksService.respondToInvitation(id, user!.email!, accept, user!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
    },
  });
};
